import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import BoothService from "../../services/booth.service";

// Common error handler
const getErrorMessage = (error) => {
  return (
    error.response?.data?.message ||
    error.message ||
    "Something went wrong"
  );
};

// GET ALL BOOTHS
export const getBooths = createAsyncThunk(
  "booth/getBooths",
  async (params = {}, thunkAPI) => {
    try {
      const response = await BoothService.getBooths(params);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to fetch booths"
        );
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// GET BOOTH BY ID
export const getBoothById = createAsyncThunk(
  "booth/getBoothById",
  async (id, thunkAPI) => {
    try {
      const response = await BoothService.getBoothById(id);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to fetch booth details"
        );
      }

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// CREATE BOOTH
export const createBooth = createAsyncThunk(
  "booth/createBooth",
  async (payload, thunkAPI) => {
    try {
      const response = await BoothService.createBooth(payload);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to create booth"
        );
      }

      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// UPDATE BOOTH
export const updateBooth = createAsyncThunk(
  "booth/updateBooth",
  async ({ id, payload }, thunkAPI) => {
    try {
      const response = await BoothService.updateBooth(id, payload);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to update booth"
        );
      }

      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

// DELETE BOOTH
export const deleteBooth = createAsyncThunk(
  "booth/deleteBooth",
  async (id, thunkAPI) => {
    try {
      const response = await BoothService.deleteBooth(id);

      if (response?.status !== 1) {
        return thunkAPI.rejectWithValue(
          response?.message || "Failed to delete booth"
        );
      }

      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialState = {
  booths: [],
  selectedBooth: null,

  loading: false,
  detailsLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  error: null,
  detailsError: null,
  successMessage: null,

  pagination: {
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
  },
};

const boothSlice = createSlice({
  name: "booth",
  initialState,

  reducers: {
    clearBoothError: (state) => {
      state.error = null;
      state.detailsError = null;
    },

    clearBoothMessage: (state) => {
      state.successMessage = null;
    },

    clearSelectedBooth: (state) => {
      state.selectedBooth = null;
    },
  },

  extraReducers: (builder) => {
    builder

      // GET ALL BOOTHS
      .addCase(getBooths.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBooths.fulfilled, (state, action) => {
        state.loading = false;

        const data = action.payload || {};

        state.booths = data.booths || [];

        state.pagination = {
          total: data.total || 0,
          currentPage: data.currentPage || 1,
          totalPages: data.totalPages || 0,
          limit: data.limit || 10,
        };
      })
      .addCase(getBooths.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // GET BOOTH BY ID
      .addCase(getBoothById.pending, (state) => {
        state.detailsLoading = true;
        state.detailsError = null;
      })
      .addCase(getBoothById.fulfilled, (state, action) => {
        state.detailsLoading = false;

        const data = action.payload || {};
        state.selectedBooth = data.booth || data;
      })
      .addCase(getBoothById.rejected, (state, action) => {
        state.detailsLoading = false;
        state.detailsError = action.payload;
      })

      // CREATE BOOTH
      .addCase(createBooth.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createBooth.fulfilled, (state, action) => {
        state.createLoading = false;
        state.successMessage =
          action.payload?.message || "Booth created successfully";

        const data = action.payload?.data;
        const booth = data?.booth || data;

        if (booth?._id) {
          state.booths.unshift(booth);
        }
      })
      .addCase(createBooth.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
      })

      // UPDATE BOOTH
      .addCase(updateBooth.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateBooth.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.successMessage =
          action.payload?.message || "Booth updated successfully";

        const data = action.payload?.data;
        const updatedBooth = data?.booth || data;

        if (updatedBooth?._id) {
          const index = state.booths.findIndex(
            (booth) => booth._id === updatedBooth._id
          );

          if (index !== -1) {
            state.booths[index] = updatedBooth;
          }

          if (state.selectedBooth?._id === updatedBooth._id) {
            state.selectedBooth = updatedBooth;
          }
        }
      })
      .addCase(updateBooth.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })

      // DELETE BOOTH
      .addCase(deleteBooth.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteBooth.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.booths = state.booths.filter(
          (booth) => booth._id !== action.payload
        );

        if (state.selectedBooth?._id === action.payload) {
          state.selectedBooth = null;
        }

        state.successMessage = "Booth deleted successfully";
      })
      .addCase(deleteBooth.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearBoothError,
  clearBoothMessage,
  clearSelectedBooth,
} = boothSlice.actions;

export default boothSlice.reducer;